import { totalBrackets } from '../constants'
import { checkMark } from '../scripts/text'
import {
  blockContainingNode,
  constrain,
  uniqueByObjItem,
} from '../scripts/utils'
import {
  getLevelsOfEachNode,
  getNumOfRoots,
  nodeTree,
} from '../segmentation/segmentation'
import isEqual from 'react-fast-compare'

const originalPseudoResults = {
  before: [],
  after: [],
}

const copy = a => JSON.parse(JSON.stringify(a))

export const lastNextLevelsArchive = {
  lastSegmentation: null,
  lastInputStartCount: null,
  lastInputEndCount: null,
}

export const pickNextLevels = (state, maxLevel, lastNextLevels = null) => {
  const { segmentation, dynamicSegmentation, selectedNodeCount, textArray } =
    state
  const pseudoResults = copy(originalPseudoResults)

  if (selectedNodeCount === null) {
    lastNextLevelsArchive.lastSegmentation = null
    lastNextLevelsArchive.lastInputStartCount = null
    lastNextLevelsArchive.lastInputEndCount = null

    return copy(originalPseudoResults)
  }

  const currentCount = selectedNodeCount
  const currentBlock = blockContainingNode(dynamicSegmentation, currentCount)

  const inputStartCount = currentBlock.startCount
  const inputEndCount = currentBlock.endCount

  if (
    lastNextLevelsArchive.lastInputStartCount === inputStartCount &&
    lastNextLevelsArchive.lastInputEndCount === inputEndCount &&
    lastNextLevels &&
    !isEqual(lastNextLevels, originalPseudoResults) &&
    isEqual(lastNextLevelsArchive.lastSegmentation, segmentation)
  ) {
    return copy(lastNextLevels)
  }

  const counts = {
    '-1': inputStartCount, // for before
    1: inputEndCount, // for after
  }

  let levelsOfEachNode = getLevelsOfEachNode()

  for (let pseudoSpeed of [-1, 1]) {
    let currentLevel = Math.max(
      maxLevel -
        levelsOfEachNode[counts[String(pseudoSpeed)]] -
        getNumOfRoots(),
      0
    )
    for (
      let pseudoLevelToMerge = currentLevel;
      pseudoLevelToMerge <= maxLevel;
      pseudoLevelToMerge++
    ) {
      if (!segmentation || !segmentation[pseudoLevelToMerge])
        return copy(originalPseudoResults)
      let seg = copy(segmentation[pseudoLevelToMerge])

      const currentStartCount = currentBlock.startCount
      const currentEndCount = currentBlock.endCount

      let targetStartCount = currentStartCount
      let targetEndCount = currentEndCount

      let targetLevelBlock = blockContainingNode(seg, currentCount)
      if (pseudoSpeed === 1) {
        // Content after
        while (
          targetLevelBlock.endCount < targetEndCount &&
          targetLevelBlock.order < Object.size(seg) - 1
        ) {
          targetLevelBlock = seg[targetLevelBlock.order + 1]
        }

        if (targetEndCount !== targetLevelBlock.endCount)
          targetEndCount = targetLevelBlock.endCount
        else
          targetEndCount =
            seg[
              constrain(
                targetLevelBlock.order + 1,
                targetLevelBlock.order,
                Object.size(seg) - 1
              )
            ].endCount

        if (targetEndCount !== currentEndCount)
          pseudoResults.after.push({
            count: targetEndCount,
            level: pseudoLevelToMerge,
          })
      } else {
        // Content before
        while (
          targetLevelBlock.startCount > targetStartCount &&
          targetLevelBlock.order > 0
        ) {
          targetLevelBlock = seg[targetLevelBlock.order - 1]
        }
        ////
        if (targetStartCount !== targetLevelBlock.startCount)
          targetStartCount = targetLevelBlock.startCount
        else
          targetStartCount =
            seg[
              constrain(targetLevelBlock.order - 1, 0, targetLevelBlock.order)
            ].startCount

        if (targetStartCount !== currentStartCount)
          pseudoResults.before.push({
            count: targetStartCount,
            level: pseudoLevelToMerge,
          })
      }
    }
  }

  const finalResults = copy(originalPseudoResults)

  // Preprocess pseudo-results
  for (let side in pseudoResults) {
    pseudoResults[side] = uniqueByObjItem(pseudoResults[side], 'count')

    const addedCount = []

    // Force-ly add a word
    // if (
    //   (side === 'before' && inputStartCount > 0) ||
    //   (side === 'after' && inputEndCount < textArray.length - 1)
    // ) {
    //   const forcedWordCount = moveToAvoidPunctuation(
    //     side === 'before' ? inputStartCount - 1 : inputEndCount + 1,
    //     side,
    //     textArray
    //   )

    //   finalResults[side].push({
    //     count: forcedWordCount,
    //     level: levelsOfEachNode[forcedWordCount],
    //     expectedSpeed: 1,
    //   })
    //   addedCount.push(forcedWordCount)
    // }

    for (let resultInd in pseudoResults[side]) {
      const { count, level } = pseudoResults[side][resultInd]

      if (
        !furtherThanAllCurrentCounts(
          addedCount,
          count,
          side === 'before' ? inputStartCount : inputEndCount,
          0
        ) ||
        (!furtherThanAllCurrentCounts(
          addedCount,
          count,
          side === 'before' ? inputStartCount : inputEndCount,
          1
        ) &&
          pseudoResults[side].slice(Number(resultInd) + 1).length >=
            totalBrackets - addedCount.length) // fine giving up some results to still have enough
      )
        continue

      // const thisCount = count
      const thisCount = moveToAvoidPunctuation(count, side, textArray, true)
      finalResults[side].push({
        count: thisCount,
        level: level,
        expectedSpeed: addedCount.length + 1,
      })
      addedCount.push(thisCount)

      if (addedCount.length === totalBrackets) break
    }
  }

  lastNextLevelsArchive.lastSegmentation = copy(segmentation)
  lastNextLevelsArchive.lastInputStartCount = inputStartCount
  lastNextLevelsArchive.lastInputEndCount = inputEndCount

  // console.log(finalResults)
  return finalResults
}

export const pickNextLevelsRecursive = (
  state,
  maxLevel,
  lastNextLevels = null
) => {
  const { segmentation, dynamicSegmentation, selectedNodeCount, textArray } =
    state

  const pseudoResults = copy(originalPseudoResults)

  if (selectedNodeCount === null || !nodeTree.current)
    return copy(originalPseudoResults)

  const currentCount = selectedNodeCount
  const currentBlock = blockContainingNode(dynamicSegmentation, currentCount)

  const inputStartCount = currentBlock.startCount
  const inputEndCount = currentBlock.endCount

  if (
    lastNextLevelsArchive.lastInputStartCount === inputStartCount &&
    lastNextLevelsArchive.lastInputEndCount === inputEndCount &&
    lastNextLevels &&
    !isEqual(lastNextLevels, originalPseudoResults) &&
    isEqual(lastNextLevelsArchive.lastSegmentation, segmentation)
  ) {
    return copy(lastNextLevels)
  }

  const counts = {
    '-1': inputStartCount, // for before
    1: inputEndCount, // for after
  }

  let levelsOfEachNode = getLevelsOfEachNode()

  const addToPseudoResults = (side, count, speed) => {
    if (count !== null) {
      pseudoResults[side].push({
        count: count,
        level: levelsOfEachNode[count],
        expectedSpeed: speed,
      })
      return speed - 1
    } else return speed
  }

  for (let pseudoSpeed of [-1, 1]) {
    const side = pseudoSpeed === -1 ? 'before' : 'after'

    let endsToFind = totalBrackets
    let inputNode, nextBracketCount

    const findNextNode = (inputCount, aggressive) => {
      inputNode = nodeTree.current.findWordNode(inputCount)
      if (inputNode) {
        nextBracketCount = inputNode.sameParentNextChild(
          pseudoSpeed,
          aggressive
        )
        if (nextBracketCount !== null && nextBracketCount !== undefined) {
          nextBracketCount = moveToAvoidPunctuation(
            nextBracketCount,
            side,
            textArray,
            true
          )
          endsToFind = addToPseudoResults(side, nextBracketCount, endsToFind)
        }
      }
    }

    findNextNode(counts[String(pseudoSpeed)], 0)
    while (
      endsToFind &&
      nextBracketCount !== null &&
      nextBracketCount !== undefined
    )
      findNextNode(nextBracketCount, 1)
  }

  lastNextLevelsArchive.lastSegmentation = copy(segmentation)
  lastNextLevelsArchive.lastInputStartCount = inputStartCount
  lastNextLevelsArchive.lastInputEndCount = inputEndCount

  // console.log(pseudoResults)
  return pseudoResults
}

/* -------------------------------------------------------------------------- */

const moveToAvoidPunctuation = (
  count,
  side,
  textArray,
  onlyCheckNext = false
) => {
  const goNext = side === 'before' ? -1 : 1
  const nextCount = Math.max(Math.min(count + goNext, textArray.length - 1), 0)
  if (
    // check next only for the after ones
    (side === 'after' && checkMark(textArray[nextCount][0])) ||
    // check current count only if `onlyCheckNext` is false
    (!onlyCheckNext && checkMark(textArray[count][0]))
  )
    return nextCount
  return count
}

const furtherThanAllCurrentCounts = (
  current,
  newCount,
  origin,
  extraGap = 1
) => {
  let further = true
  for (let count of current) {
    if (Math.abs(count - origin) + extraGap >= Math.abs(newCount - origin))
      further = false
  }
  return further
}
